<template>
  <div class="sa-map">
    <div ref="mapEl" class="sa-map__canvas" role="application" aria-label="Mapa dos sensores" />
    <p v-if="!markers.length" class="sa-map__empty">Nenhum sensor para exibir no mapa.</p>
  </div>
</template>

<script>
import L from "leaflet"
import "leaflet/dist/leaflet.css"

const DEFAULT_CENTER = [-15.13, -53.19]
const DEFAULT_ZOOM = 4
/** Zoom em que a escala do Leaflet fica em 1 km, em vez de aproximar até ~50 m. */
const ONE_KM_ZOOM = 13

export default {
  name: "SensorsAmbienteMap",
  props: {
    markers: {
      type: Array,
      default: () => [],
    },
    selectedPk: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      map: null,
      markerLayer: null,
      markerByPk: {},
    }
  },
  watch: {
    markers: {
      handler() {
        this.$nextTick(() => this.renderMarkers())
      },
      deep: true,
    },
    selectedPk() {
      this.$nextTick(() => {
        this.highlightSelected(true)
      })
    },
  },
  mounted() {
    this.bootMap()
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    bootMap() {
      this.$nextTick(() => {
        this.initMap()
        this.renderMarkers()
        window.setTimeout(() => this.invalidateSize(), 200)
      })
    },
    initMap() {
      if (!this.$refs.mapEl || this.map) return

      this.map = L.map(this.$refs.mapEl, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        scrollWheelZoom: true,
      })

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(this.map)
      L.control.scale({ imperial: false, metric: true }).addTo(this.map)

      this.markerLayer = L.layerGroup().addTo(this.map)
    },
    formatReadAt(readAt) {
      if (!(readAt instanceof Date) || Number.isNaN(readAt.getTime())) return "—"
      try {
        return readAt.toLocaleString("pt-BR")
      } catch {
        return "—"
      }
    },
    buildTooltipHtml(marker) {
      const name = marker.name || "Sensor"
      if (marker.error) {
        return `<strong>${this.escapeHtml(name)}</strong><br/>Sem dados recentes`
      }
      const temp =
        marker.temperature != null && !Number.isNaN(Number(marker.temperature))
          ? `${Number(marker.temperature).toFixed(1)} ºC`
          : "—"
      const hum =
        marker.humidity != null && !Number.isNaN(Number(marker.humidity))
          ? `${Number(marker.humidity).toFixed(1)} % UR`
          : "—"
      const when = this.formatReadAt(marker.readAt)
      const gpsNote = marker.hasGps ? "" : "<br/><em>Posição aproximada</em>"
      return `<strong>${this.escapeHtml(name)}</strong><br/>Temp.: ${temp}<br/>Umid.: ${hum}<br/>${when}${gpsNote}`
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
    },
    createIcon(isSelected) {
      return L.divIcon({
        className: isSelected ? "sa-map-pin sa-map-pin--selected" : "sa-map-pin",
        html: '<span class="sa-map-pin__dot"></span>',
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      })
    },
    renderMarkers() {
      if (!this.map || !this.markerLayer) return
      this.markerLayer.clearLayers()
      this.markerByPk = {}
      const bounds = []
      for (const marker of this.markers) {
        if (marker.lat == null || marker.lng == null) continue
        const lat = Number(marker.lat)
        const lng = Number(marker.lng)
        if (Number.isNaN(lat) || Number.isNaN(lng)) continue
        const isSelected =
          marker.pk != null &&
          this.selectedPk != null &&
          String(marker.pk) === String(this.selectedPk)
        const leafletMarker = L.marker([lat, lng], {
          icon: this.createIcon(isSelected),
          zIndexOffset: isSelected ? 1000 : 0,
        })
        leafletMarker.bindTooltip(this.buildTooltipHtml(marker), {
          direction: "top",
          offset: [0, -10],
          opacity: 0.95,
        })
        leafletMarker.on("click", () => {
          this.$emit("select", marker.pk)
        })
        leafletMarker.addTo(this.markerLayer)
        this.markerByPk[marker.pk] = leafletMarker
        bounds.push([lat, lng])
      }
      this.fitMapToBounds(bounds)
      this.highlightSelected(false)
      this.$nextTick(() => {
        this.invalidateSize()
        if (bounds.length > 1) {
          window.setTimeout(() => this.fitMapToBounds(bounds), 150)
        }
      })
    },
    fitMapToBounds(bounds) {
      if (!this.map || !bounds.length) {
        if (this.map) this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM)
        return
      }
      if (bounds.length === 1) {
        this.map.setView(bounds[0], ONE_KM_ZOOM)
        return
      }
      this.map.fitBounds(L.latLngBounds(bounds), { padding: [52, 52], maxZoom: ONE_KM_ZOOM })
    },
    invalidateSize() {
      if (this.map) this.map.invalidateSize()
    },
    highlightSelected(panToSelected = false) {
      for (const [pk, leafletMarker] of Object.entries(this.markerByPk)) {
        const isSelected =
          this.selectedPk != null && String(pk) === String(this.selectedPk)
        leafletMarker.setIcon(this.createIcon(isSelected))
        leafletMarker.setZIndexOffset(isSelected ? 1000 : 0)
        if (panToSelected && isSelected && this.map) {
          this.map.panTo(leafletMarker.getLatLng(), { animate: true, duration: 0.35 })
        }
      }
    },
  },
}
</script>

<style scoped>
.sa-map {
  position: relative;
  width: 100%;
  height: 420px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #eef2f4;
}

.sa-map__canvas {
  width: 100%;
  height: 420px;
  z-index: 0;
}


.sa-map__empty {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  text-align: center;
  font-size: 0.85rem;
  color: #6c757d;
  background: rgba(255, 255, 255, 0.92);
  z-index: 1;
}
</style>

<style>
.sa-map-pin {
  background: transparent;
  border: none;
}

.sa-map-pin__dot {
  display: block;
  width: 14px;
  height: 14px;
  margin: 4px;
  border-radius: 50%;
  background: #2b7abf;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.5);
}

.sa-map-pin--selected .sa-map-pin__dot {
  width: 18px;
  height: 18px;
  margin: 2px;
  background: #78b843;
  border-width: 3px;
  box-shadow:
    0 0 0 2px rgba(0, 0, 0, 0.4),
    0 0 0 5px rgba(120, 184, 67, 0.45),
    0 2px 10px rgba(0, 0, 0, 0.55);
}

/* Bootstrap (img { max-width: 100% }) impede os tiles do Leaflet de renderizar. */
.sa-map .leaflet-container {
  width: 100%;
  height: 420px;
  font-family: inherit;
  background: #e4e7ea;
}

.sa-map .leaflet-tile-pane img,
.sa-map img.leaflet-tile,
.sa-map img.leaflet-marker-icon,
.sa-map img.leaflet-marker-shadow {
  max-width: none !important;
  max-height: none !important;
  width: auto !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
}

.sa-map .leaflet-control-zoom a {
  color: #1b4238;
}
</style>
